import {useRef, useState} from "react";
import {Theme} from "@emotion/react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {onDesktop} from "@pg-design/helpers-css";
import {InputRange, InputWrapper, IRange} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form/index";

import {useClickOutside} from "../hooks/handle_click_outside";
import {Filter} from "../search/components/filters/SearchFilter";
import {FilterFooter} from "../search/components/filters/SearchFiltersFooter";
import {SearchFiltersRooms} from "../search/components/filters/SearchFiltersRooms";
import {IFilterChildProps, RangeFilter} from "../search/components/filters/SearchRangeFilter";
import {FloorChoicesSwitch} from "../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../search/constants/DropdownToOpen";
import {getAreaFilterLabel, getFloorChoiceLabel} from "../search/utils/get_label";
import {getRoomLabel} from "../search/utils/room_field_data_translations";

enum Round {
    PRICE = 10000,
    AREA = 5,
    PRICE_M2 = 500
}

interface IOwnProps {
    availableRooms?: number[];
    shouldShowFloorsFilter: boolean;
    setDesktopFilterBlendActive?: (isOpen: boolean) => void;
    onMobileCloseModal?: () => void;
    onMobileResetForm?: () => void;
    isMobile?: boolean;
    className?: string;
}

export interface ISearchFiltersFormValues {
    area: IRange<string>;
    price: IRange<string>;
    rooms: IRange<number | "">;
    floor_choices: number[];
}

interface IProps extends IOwnProps, IFormProps<ISearchFiltersFormValues> {}

const SearchFiltersC = (props: IProps) => {
    const {isMobile = false} = props;
    /**
     * Ref's
     */
    const filtersWrapperRef = useRef<HTMLElement | null>(null);
    const filtersWrapperRefHandle = (elem: HTMLElement | null) => {
        filtersWrapperRef.current = elem;
    };

    const FilterWrapper = props.isMobile ? NoWrapper : InputWrapper;

    /**
     * State
     */
    const [currentOpenFilter, setCurrentOpenFilter] = useState<DropdownToOpen | null | undefined>(null);

    /**
     * Utils
     */

    useClickOutside(filtersWrapperRef, () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
    });

    /**
     * Mobile specific
     */

    const closeMobileModal = () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
        props.onMobileCloseModal && props.onMobileCloseModal();
    };

    const submitMobileModal = (e: React.MouseEvent<HTMLElement>) => {
        closeMobileModal();
        props.onSubmit && props.onSubmit(e);
    };

    /**
     * General
     */

    const onDropdownOpen = (dropdownToOpen: DropdownToOpen | null) => {
        const shouldOpen = typeof dropdownToOpen === "number" && dropdownToOpen >= 0;

        if (shouldOpen) {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(true);
            setCurrentOpenFilter(dropdownToOpen);
        } else {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
            setCurrentOpenFilter(null);
        }
    };

    /**
     * Render
     */

    const areaProps = props.getFieldProps("area");
    const roomsProps = props.getFieldProps("rooms");
    const floorChoicesProps = props.getFieldProps("floor_choices");
    const availableRooms = props.availableRooms;

    return (
        <FiltersHolder shouldShowFloorsFilter={props.shouldShowFloorsFilter} ref={filtersWrapperRefHandle} className={props.className}>
            <div css={filtersScroller}>
                <FilterWrapper css={inputWrapperStyles}>
                    <RangeFilter
                        label="Pokoje"
                        id="roomFilter"
                        {...roomsProps}
                        getLabel={getRoomLabel}
                        labelId="room-filter-field"
                        errorOnBottom
                        isDropdownOpened={currentOpenFilter === DropdownToOpen.ROOMS}
                        toggleDropdown={onDropdownOpen}
                        dropdownName={DropdownToOpen.ROOMS}
                        isMobileWidth={isMobile}
                    >
                        {(props: IFilterChildProps<number | "">) => <SearchFiltersRooms rooms={availableRooms} {...props} showFooter={!isMobile} />}
                    </RangeFilter>
                </FilterWrapper>
                {props.shouldShowFloorsFilter && (
                    <FilterWrapper css={inputWrapperStyles}>
                        <Filter
                            label="Piętro"
                            id="floorChoicesFilter"
                            {...floorChoicesProps}
                            getLabel={getFloorChoiceLabel}
                            labelId="floor_choices-filter-field"
                            errorOnBottom
                            isDropdownOpened={currentOpenFilter === DropdownToOpen.FLOORS}
                            setDropdownOpen={onDropdownOpen}
                            dropdownName={DropdownToOpen.FLOORS}
                            isMobileWidth={isMobile}
                        >
                            {(props) => (
                                <>
                                    <FloorChoicesSwitch {...props} />

                                    {!isMobile && (
                                        <FilterFooter
                                            onCancel={() => {
                                                props.clearField();
                                                onDropdownOpen(null);
                                            }}
                                            onSave={() => {
                                                props.onChange(props.name, props.value);
                                                props.onAfterChange?.(props.name, props.value);
                                                onDropdownOpen(null);
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </Filter>{" "}
                    </FilterWrapper>
                )}
                <FilterWrapper css={inputWrapperStyles}>
                    <RangeFilter
                        label="Metraż"
                        errorOnBottom
                        id="areaFilter"
                        {...areaProps}
                        onChange={areaProps.onChange}
                        onAfterChange={areaProps.onAfterChange}
                        getLabel={getAreaFilterLabel}
                        isDropdownOpened={currentOpenFilter === DropdownToOpen.AREA}
                        toggleDropdown={onDropdownOpen}
                        dropdownName={DropdownToOpen.AREA}
                        isMobileWidth={isMobile}
                    >
                        {(props: IFilterChildProps<string>) => (
                            <>
                                <Text variant="body_copy_1" as="p" mb="1.6rem">
                                    {props.label}
                                </Text>
                                <InputRange {...props} onChange={props.onChange} hasDashSeparator roundToMultiplicationOf={Round.AREA} />

                                {!isMobile && (
                                    <FilterFooter
                                        onCancel={() => {
                                            props.clearField();
                                            props.toggleDropdown();
                                        }}
                                        onSave={() => {
                                            props.onChange(props.name, props.value);
                                            props.onAfterChange?.(props.name, props.value);
                                            props.toggleDropdown();
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </RangeFilter>
                </FilterWrapper>
            </div>

            <div css={mobileFiltersFooterWrapper}>
                <FilterFooter
                    onCancel={props.onMobileResetForm || (() => null)}
                    onSave={(e: React.MouseEvent<HTMLElement>) => {
                        submitMobileModal(e);
                    }}
                />
            </div>
        </FiltersHolder>
    );
};

export const PropertyListFilters = form<ISearchFiltersFormValues, IProps>(SearchFiltersC);

const FiltersHolder = styled.div<{shouldShowFloorsFilter: boolean}>`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 6rem);

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        flex-grow: 1;
        flex-shrink: 1;
        width: 50%;
        height: unset;

        div:nth-of-type(${(props) => (props.shouldShowFloorsFilter ? 4 : 3)}) {
            margin-right: 0;
        }
    }
`;

const filtersScroller = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        overflow: initial;
    }
`;

const mobileFiltersFooterWrapper = (theme: Theme) => css`
    width: 100%;
    display: block;
    padding: 0 2rem 2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const inputWrapperStyles = css`
    padding: 0;
    margin-right: 1.6rem;

    ${onDesktop(css`
        flex: 1 1 20%;
    `)}
`;

const NoWrapper = styled.div`
    display: flex;
`;
import {useRef, useState} from "react";
import {Theme} from "@emotion/react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {onDesktop} from "@pg-design/helpers-css";
import {InputRange, InputWrapper, IRange} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form/index";

import {useClickOutside} from "../hooks/handle_click_outside";
import {Filter} from "../search/components/filters/SearchFilter";
import {FilterFooter} from "../search/components/filters/SearchFiltersFooter";
import {SearchFiltersRooms} from "../search/components/filters/SearchFiltersRooms";
import {IFilterChildProps, RangeFilter} from "../search/components/filters/SearchRangeFilter";
import {FloorChoicesSwitch} from "../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../search/constants/DropdownToOpen";
import {getAreaFilterLabel, getFloorChoiceLabel} from "../search/utils/get_label";
import {getRoomLabel} from "../search/utils/room_field_data_translations";

enum Round {
    PRICE = 10000,
    AREA = 5,
    PRICE_M2 = 500
}

interface IOwnProps {
    availableRooms?: number[];
    shouldShowFloorsFilter: boolean;
    setDesktopFilterBlendActive?: (isOpen: boolean) => void;
    onMobileCloseModal?: () => void;
    onMobileResetForm?: () => void;
    isMobile?: boolean;
    className?: string;
}

export interface ISearchFiltersFormValues {
    area: IRange<string>;
    price: IRange<string>;
    rooms: IRange<number | "">;
    floor_choices: number[];
}

interface IProps extends IOwnProps, IFormProps<ISearchFiltersFormValues> {}

const SearchFiltersC = (props: IProps) => {
    const {isMobile = false} = props;
    /**
     * Ref's
     */
    const filtersWrapperRef = useRef<HTMLElement | null>(null);
    const filtersWrapperRefHandle = (elem: HTMLElement | null) => {
        filtersWrapperRef.current = elem;
    };

    const FilterWrapper = props.isMobile ? NoWrapper : InputWrapper;

    /**
     * State
     */
    const [currentOpenFilter, setCurrentOpenFilter] = useState<DropdownToOpen | null | undefined>(null);

    /**
     * Utils
     */

    useClickOutside(filtersWrapperRef, () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
    });

    /**
     * Mobile specific
     */

    const closeMobileModal = () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
        props.onMobileCloseModal && props.onMobileCloseModal();
    };

    const submitMobileModal = (e: React.MouseEvent<HTMLElement>) => {
        closeMobileModal();
        props.onSubmit && props.onSubmit(e);
    };

    /**
     * General
     */

    const onDropdownOpen = (dropdownToOpen: DropdownToOpen | null) => {
        const shouldOpen = typeof dropdownToOpen === "number" && dropdownToOpen >= 0;

        if (shouldOpen) {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(true);
            setCurrentOpenFilter(dropdownToOpen);
        } else {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
            setCurrentOpenFilter(null);
        }
    };

    /**
     * Render
     */

    const areaProps = props.getFieldProps("area");
    const roomsProps = props.getFieldProps("rooms");
    const floorChoicesProps = props.getFieldProps("floor_choices");
    const availableRooms = props.availableRooms;

    return (
        <FiltersHolder shouldShowFloorsFilter={props.shouldShowFloorsFilter} ref={filtersWrapperRefHandle} className={props.className}>
            <div css={filtersScroller}>
                <FilterWrapper css={inputWrapperStyles}>
                    <RangeFilter
                        label="Pokoje"
                        id="roomFilter"
                        {...roomsProps}
                        getLabel={getRoomLabel}
                        labelId="room-filter-field"
                        errorOnBottom
                        isDropdownOpened={currentOpenFilter === DropdownToOpen.ROOMS}
                        toggleDropdown={onDropdownOpen}
                        dropdownName={DropdownToOpen.ROOMS}
                        isMobileWidth={isMobile}
                    >
                        {(props: IFilterChildProps<number | "">) => <SearchFiltersRooms rooms={availableRooms} {...props} showFooter={!isMobile} />}
                    </RangeFilter>
                </FilterWrapper>
                {props.shouldShowFloorsFilter && (
                    <FilterWrapper css={inputWrapperStyles}>
                        <Filter
                            label="Piętro"
                            id="floorChoicesFilter"
                            {...floorChoicesProps}
                            getLabel={getFloorChoiceLabel}
                            labelId="floor_choices-filter-field"
                            errorOnBottom
                            isDropdownOpened={currentOpenFilter === DropdownToOpen.FLOORS}
                            setDropdownOpen={onDropdownOpen}
                            dropdownName={DropdownToOpen.FLOORS}
                            isMobileWidth={isMobile}
                        >
                            {(props) => (
                                <>
                                    <FloorChoicesSwitch {...props} />

                                    {!isMobile && (
                                        <FilterFooter
                                            onCancel={() => {
                                                props.clearField();
                                                onDropdownOpen(null);
                                            }}
                                            onSave={() => {
                                                props.onChange(props.name, props.value);
                                                props.onAfterChange?.(props.name, props.value);
                                                onDropdownOpen(null);
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </Filter>{" "}
                    </FilterWrapper>
                )}
                <FilterWrapper css={inputWrapperStyles}>
                    <RangeFilter
                        label="Metraż"
                        errorOnBottom
                        id="areaFilter"
                        {...areaProps}
                        onChange={areaProps.onChange}
                        onAfterChange={areaProps.onAfterChange}
                        getLabel={getAreaFilterLabel}
                        isDropdownOpened={currentOpenFilter === DropdownToOpen.AREA}
                        toggleDropdown={onDropdownOpen}
                        dropdownName={DropdownToOpen.AREA}
                        isMobileWidth={isMobile}
                    >
                        {(props: IFilterChildProps<string>) => (
                            <>
                                <Text variant="body_copy_1" as="p" mb="1.6rem">
                                    {props.label}
                                </Text>
                                <InputRange {...props} onChange={props.onChange} hasDashSeparator roundToMultiplicationOf={Round.AREA} />

                                {!isMobile && (
                                    <FilterFooter
                                        onCancel={() => {
                                            props.clearField();
                                            props.toggleDropdown();
                                        }}
                                        onSave={() => {
                                            props.onChange(props.name, props.value);
                                            props.onAfterChange?.(props.name, props.value);
                                            props.toggleDropdown();
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </RangeFilter>
                </FilterWrapper>
            </div>

            <div css={mobileFiltersFooterWrapper}>
                <FilterFooter
                    onCancel={props.onMobileResetForm || (() => null)}
                    onSave={(e: React.MouseEvent<HTMLElement>) => {
                        submitMobileModal(e);
                    }}
                />
            </div>
        </FiltersHolder>
    );
};

export const PropertyListFilters = form<ISearchFiltersFormValues, IProps>(SearchFiltersC);

const FiltersHolder = styled.div<{shouldShowFloorsFilter: boolean}>`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 6rem);

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        justify-content: space-between;
        flex-direction: row;
        align-items: center;
        flex-grow: 1;
        flex-shrink: 1;
        width: 50%;
        height: unset;

        div:nth-of-type(${(props) => (props.shouldShowFloorsFilter ? 4 : 3)}) {
            margin-right: 0;
        }
    }
`;

const filtersScroller = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        overflow: initial;
    }
`;

const mobileFiltersFooterWrapper = (theme: Theme) => css`
    width: 100%;
    display: block;
    padding: 0 2rem 2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const inputWrapperStyles = css`
    padding: 0;
    margin-right: 1.6rem;

    ${onDesktop(css`
        flex: 1 1 20%;
    `)}
`;

const NoWrapper = styled.div`
    display: flex;
`;
